<script setup>
import { defineProps, defineEmits, ref, computed, onMounted, watch } from "vue";
import Card from "primevue/card";
import Button from "primevue/button";
import FileUpload from "primevue/fileupload";
import Dialog from "primevue/dialog";
import Calendar from "primevue/calendar";
import Chip from "primevue/chip";
import InputText from "primevue/inputtext";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Dropdown from "primevue/dropdown";
import { dashboardAPI } from "../service/api.js";

const props = defineProps({
  customers: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(["update", "error"]);

const showDatePicker = ref(false);
const selectedDate = ref(null);
const pendingUpload = ref(null);
const currentCustomerId = ref(null);
const selectedEmployerCode = ref(null);
const employersByCustomerId = ref({});
const newEmployerCodes = ref({});
const showInvoiceDatePicker = ref(false);
const invoiceStartDate = ref(null);
const invoiceEndDate = ref(null);
const currentInvoiceCustomerId = ref(null);
const showValidationErrorDialog = ref(false);
const validationError = ref({
  title: '',
  message: '',
  data: [],
  columns: []
});

// Error configuration mapping for different validation error types
const errorConfigs = {
  "Failed to resolve vendors": {
    title: "Unresolved Vendors",
    message: "The following transactions could not be matched to vendors in the system. Please verify the vendor IDs and tax IDs are correct in your vendor database.",
    columns: [
      { field: "external_trans_id", header: "External Trans ID", width: "40%" },
      { field: "carepay_vendor_id", header: "CarePay Vendor ID", width: "30%" },
      { field: "company_tax_id", header: "Company Tax ID", width: "30%" }
    ],
    dataKey: "unresolved_vendors"
  },
  "Invalid payment types": {
    title: "Invalid Payment Types",
    message: "The following transactions have vendors with invalid or missing payment types. Please update the vendor payment types in the vendor database.",
    columns: [
      { field: "external_trans_id", header: "External Trans ID", width: "35%" },
      { field: "vendor_id", header: "Vendor ID", width: "35%" },
      { field: "attribute_value", header: "Payment Type", width: "30%" }
    ],
    dataKey: "invalid_payment_types"
  },
  "Invalid batch preferences": {
    title: "Invalid Batch Payment Preferences",
    message: "The following transactions have vendors with invalid or missing batch payment preferences. Please update the vendor batch preferences in the vendor database.",
    columns: [
      { field: "external_trans_id", header: "External Trans ID", width: "35%" },
      { field: "vendor_id", header: "Vendor ID", width: "35%" },
      { field: "attribute_value", header: "Batch Preference", width: "30%" }
    ],
    dataKey: "invalid_payment_types"
  }
};

const fetchEmployersForCustomers = async () => {
  for (const customer of props.customers) {
    try {
      const response = await dashboardAPI.listEmployersByCustomerId(
        customer.id
      );
      const employers = response.data;
      if (!Array.isArray(employers)) {
        emit("error", new Error("Invalid data format"));
      }
      const codes = employers.map((employer) => employer.employer_code);
      employersByCustomerId.value[customer.id] = codes;
    } catch (error) {
      console.error(
        `Error fetching employers for customer ${customer.id}:`,
        error
      );
      emit("error", error);
    }
  }
};

onMounted(() => {
  if (props.customers.length > 0) {
    fetchEmployersForCustomers();
  }
});

watch(
  () => props.customers,
  (newCustomers) => {
    if (newCustomers.length > 0) {
      fetchEmployersForCustomers();
    }
  },
  { deep: true }
);

const addEmployer = async (customerId) => {
  const employerCode = newEmployerCodes.value[customerId];
  if (!employerCode || !employerCode.trim()) return;

  try {
    await dashboardAPI.addEmployer(customerId, employerCode.trim());
    await fetchEmployersForCustomers();
    newEmployerCodes.value[customerId] = "";
    emit("update");
  } catch (error) {
    console.error("Error adding employer:", error);
    emit("error", error);
  }
};

const deleteEmployer = async (customerId, employerCode) => {
  try {
    await dashboardAPI.deleteEmployer(customerId, employerCode);
    await fetchEmployersForCustomers();
    emit("update");
  } catch (error) {
    console.error("Error deleting employer:", error);
    emit("error", error);
  }
};

const currentCustomer = computed(() =>
  props.customers.find((c) => c.id === currentCustomerId.value)
);

const isSingleEmployerUpload = computed(
  () => currentCustomer.value?.payment_file_mode === "SINGLE_EMPLOYER"
);

const uploadEmployerOptions = computed(() => {
  if (!currentCustomerId.value) return [];
  return employersByCustomerId.value[currentCustomerId.value] || [];
});

const handleFileSelect = (customerId, event) => {
  currentCustomerId.value = customerId;
  pendingUpload.value = event.files[0];
  selectedDate.value = null;
  selectedEmployerCode.value = null;

  const customer = props.customers.find((c) => c.id === customerId);
  if (customer?.payment_file_mode === "SINGLE_EMPLOYER") {
    const codes = employersByCustomerId.value[customerId] || [];
    const fileName = (event.files[0]?.name || "").toLowerCase();
    const matches = codes.filter((code) =>
      fileName.includes(code.toLowerCase())
    );
    if (matches.length > 0) {
      selectedEmployerCode.value = matches[0];
    }
  }

  showDatePicker.value = true;
};

const canConfirmUpload = computed(() => {
  if (!selectedDate.value || !pendingUpload.value) return false;
  if (isSingleEmployerUpload.value && !selectedEmployerCode.value)
    return false;
  return true;
});

const confirmUpload = async () => {
  if (!canConfirmUpload.value) return;

  try {
    console.log("Starting upload...");
    const employerCode = isSingleEmployerUpload.value
      ? selectedEmployerCode.value
      : null;
    await dashboardAPI.uploadPaymentFile(
      currentCustomerId.value,
      selectedDate.value,
      pendingUpload.value,
      employerCode
    );
    console.log("Upload successful");
    emit("update");
    showDatePicker.value = false;
    pendingUpload.value = null;
    currentCustomerId.value = null;
    selectedDate.value = null;
    selectedEmployerCode.value = null;
  } catch (error) {
    // Check if it's a validation error (422 response)
    if (error.isValidationError) {
      const config = errorConfigs[error.errorType];
      if (config) {
        validationError.value = {
          title: config.title,
          message: config.message,
          data: error.details[config.dataKey],
          columns: config.columns
        };
        showValidationErrorDialog.value = true;
      } else {
        // Fallback if we don't have a config for this error type
        emit("error", error);
      }
    } else {
      // Emit regular error to parent for display
      emit("error", error);
    }
    // Reset the upload state
    showDatePicker.value = false;
    pendingUpload.value = null;
    currentCustomerId.value = null;
    selectedDate.value = null;
    selectedEmployerCode.value = null;
  }
};

const cancelUpload = () => {
  showDatePicker.value = false;
  pendingUpload.value = null;
  currentCustomerId.value = null;
  selectedDate.value = null;
  selectedEmployerCode.value = null;
};

const downloadConfirmationFile = (id, full = false) => {
  dashboardAPI
    .downloadConfirmationFile(id, full)
    .then((response) => {
      // Get the filename from the Content-Disposition header
      const disposition = response.headers["content-disposition"];

      let filename = "file.csv"; // Default filename

      if (disposition && disposition.indexOf("attachment") !== -1) {
        const matches = disposition.match(/filename="?(.+)"?/);
        if (matches[1]) {
          filename = matches[1];
        }
      }

      // Create a URL for the Blob and initiate the download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename); // Use the filename from the header
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link); // Clean up the DOM
    })
    .catch((error) => {
      console.error("Error downloading the file", error);
    });
};

const handleInvoiceExport = (customerId) => {
  currentInvoiceCustomerId.value = customerId;
  invoiceStartDate.value = null;
  invoiceEndDate.value = null;
  showInvoiceDatePicker.value = true;
};

const confirmInvoiceExport = async () => {
  if (!invoiceStartDate.value || !invoiceEndDate.value) return;

  try {
    const response = await dashboardAPI.downloadInvoiceExport(
      currentInvoiceCustomerId.value,
      invoiceStartDate.value,
      invoiceEndDate.value
    );

    // Extract filename from Content-Disposition header
    const disposition = response.headers["content-disposition"];
    let filename = "invoice_export.csv";
    if (disposition && disposition.indexOf("attachment") !== -1) {
      const matches = disposition.match(/filename="?(.+)"?/);
      if (matches[1]) filename = matches[1];
    }

    // Download the file
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showInvoiceDatePicker.value = false;
    invoiceStartDate.value = null;
    invoiceEndDate.value = null;
    currentInvoiceCustomerId.value = null;
  } catch (error) {
    emit("error", error);
    showInvoiceDatePicker.value = false;
  }
};

const cancelInvoiceExport = () => {
  showInvoiceDatePicker.value = false;
  invoiceStartDate.value = null;
  invoiceEndDate.value = null;
  currentInvoiceCustomerId.value = null;
};

const downloadPaymentAcknowledgement = (customerId, includeExported = false) => {
  dashboardAPI
    .exportPaymentAcknowledgement(customerId, includeExported)
    .then((response) => {
      const disposition = response.headers["content-disposition"];
      let filename = "payment_acknowledgement.csv";
      if (disposition && disposition.indexOf("attachment") !== -1) {
        const matches = disposition.match(/filename="?(.+)"?/);
        if (matches && matches[1]) filename = matches[1];
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    })
    .catch((error) => {
      console.error("Error downloading payment acknowledgement", error);
      emit("error", error);
    });
};
</script>
<template>
  <div class="card-container">
    <Card
      v-for="customer in customers"
      :key="customer.id"
      style="width: 25rem; overflow: hidden"
    >
      <template #title>{{ customer.name }}</template>
      <template #content>
        <div class="employer-section mb-3">
          <label class="block mb-2 font-medium">Employer Codes:</label>
          <div class="flex flex-wrap gap-2 mb-2">
            <Chip
              v-for="code in employersByCustomerId[customer.id] || []"
              :key="code"
              :label="code"
              removable
              @remove="deleteEmployer(customer.id, code)"
            />
          </div>
          <div class="flex gap-2">
            <InputText
              v-model="newEmployerCodes[customer.id]"
              placeholder="Add Employer Code"
              @keyup.enter="addEmployer(customer.id)"
              class="flex-grow-1"
            />
            <Button
              icon="pi pi-plus"
              @click="addEmployer(customer.id)"
              :disabled="!newEmployerCodes[customer.id]?.trim()"
            />
          </div>
        </div>
        <p class="m-0 flex gap-2">
          <Button @click="downloadConfirmationFile(customer.id)">
            <span class="pi pi-download" />
            Confirmation
          </Button>
          <Button @click="downloadConfirmationFile(customer.id, true)">
            <span class="pi pi-download" />
            Confirmation (all)
          </Button>
        </p>
        <p class="m-0">
          <Button @click="handleInvoiceExport(customer.id)">
            <span class="pi pi-file-export" />
            Invoice Export
          </Button>
        </p>
        <p class="m-0 flex gap-2">
          <Button @click="downloadPaymentAcknowledgement(customer.id)">
            <span class="pi pi-file-export" />
            Export Acknowledgement
          </Button>
          <Button
            @click="downloadPaymentAcknowledgement(customer.id, true)"
            severity="secondary"
          >
            <span class="pi pi-refresh" />
            Re-export Acknowledgement
          </Button>
        </p>
        <p class="m-0">
          <FileUpload
            mode="basic"
            name="paymentFile"
            @select="(event) => handleFileSelect(customer.id, event)"
            customUpload
            :auto="true"
            chooseLabel="Upload Payment File"
          />
        </p>
      </template>
    </Card>
  </div>

  <Dialog
    v-model:visible="showDatePicker"
    modal
    header="Upload Payment File"
    :style="{ width: '25rem' }"
  >
    <div class="flex flex-column gap-3">
      <div v-if="isSingleEmployerUpload">
        <label for="employer-code">Employer Code</label>
        <Dropdown
          id="employer-code"
          v-model="selectedEmployerCode"
          :options="uploadEmployerOptions"
          placeholder="Select employer"
          class="w-full"
        />
        <small v-if="pendingUpload?.name" class="text-color-secondary">
          Suggested from file name when it matches an employer code.
        </small>
      </div>
      <div>
        <label for="register-date">Register Date</label>
        <Calendar
          v-model="selectedDate"
          inputId="register-date"
          dateFormat="yy-mm-dd"
          showIcon
        />
      </div>
    </div>
    <template #footer>
      <Button label="Cancel" @click="cancelUpload" text />
      <Button
        label="Upload"
        @click="confirmUpload"
        :disabled="!canConfirmUpload"
      />
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showInvoiceDatePicker"
    modal
    header="Export Invoice - Select Date Range"
    :style="{ width: '30rem' }"
  >
    <div class="flex flex-column gap-3">
      <div>
        <label for="invoice-start-date" class="block mb-2">Start Date</label>
        <Calendar
          v-model="invoiceStartDate"
          inputId="invoice-start-date"
          dateFormat="yy-mm-dd"
          showIcon
        />
      </div>
      <div>
        <label for="invoice-end-date" class="block mb-2">End Date</label>
        <Calendar
          v-model="invoiceEndDate"
          inputId="invoice-end-date"
          dateFormat="yy-mm-dd"
          showIcon
        />
      </div>
    </div>
    <template #footer>
      <Button label="Cancel" @click="cancelInvoiceExport" text />
      <Button
        label="Export"
        @click="confirmInvoiceExport"
        :disabled="!invoiceStartDate || !invoiceEndDate"
      />
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showValidationErrorDialog"
    modal
    :style="{ width: '60rem' }"
  >
    <template #header>
      <div class="flex align-items-center gap-2">
        <i class="pi pi-times-circle" style="color: #ef4444; font-size: 1.5rem"></i>
        <span style="color: #ef4444; font-weight: 600">{{ validationError.title }}</span>
      </div>
    </template>
    <p class="mb-3" style="color: #dc2626">
      {{ validationError.message }}
    </p>
    <DataTable :value="validationError.data" stripedRows paginator :rows="10">
      <Column
        v-for="col in validationError.columns"
        :key="col.field"
        :field="col.field"
        :header="col.header"
        :style="{ width: col.width }"
      />
    </DataTable>
    <template #footer>
      <Button
        label="Close"
        @click="showValidationErrorDialog = false"
        severity="secondary"
        autofocus
      />
    </template>
  </Dialog>
</template>

<style scoped>
.card-container {
  margin: 0 auto;
  padding: 1rem;
}

.employer-section {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
}
</style>
